import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase.js';
import { migrateGuestData } from '../lib/guestMigration.js';
import { queryClient } from '../lib/queryClient.js';
import { briefStore } from '../lib/briefStore.js';
import { onSignIn as subOnSignIn, onSignOut as subOnSignOut } from '../lib/subscription.js';
import { configurePurchases, logoutPurchases } from '../lib/purchases.js';

// Hook for auth state. Handles session bootstrap, auth subscription, and
// guest→account migration on sign-in.
export function useAuth() {
  const [status, setStatus] = useState('loading'); // 'loading' | 'guest' | 'signed-in'
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [migration, setMigration] = useState({ state: 'idle', error: null }); // idle | running | ok | failed

  useEffect(() => {
    let mounted = true;

    // Race the network call against a 5s timeout so a hanging request can
    // never leave the app stuck on the loading screen — which is exactly
    // how an iPad reviewer would see a blank WebView (Apple 2.1(a)). On
    // timeout we fall back to guest mode; the auth subscription below will
    // still pick up a real session if/when the network call eventually
    // resolves.
    const sessionPromise = supabase.auth.getSession()
      .then(({ data }) => ({ session: data?.session || null, timedOut: false }))
      .catch((err) => {
        console.error('[auth] getSession failed', err);
        return { session: null, timedOut: false, error: err };
      });
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => resolve({ session: null, timedOut: true }), 5000);
    });
    Promise.race([sessionPromise, timeoutPromise]).then(({ session, timedOut }) => {
      if (!mounted) return;
      if (timedOut) console.warn('[auth] getSession timed out after 5s, continuing as guest');
      setSession(session || null);
      setUser(session?.user || null);
      setStatus(session ? 'signed-in' : 'guest');
      // Scope local brief storage to this user on first boot so a resumed
      // session sees the right bucket before any other effect runs.
      briefStore.setUser(session?.user?.id || 'guest');
      // Bootstrap subscription state too — refresh entitlements from the
      // profiles row and configure RevenueCat with the user_id so any
      // purchase the user makes attaches to the right account.
      if (session?.user) {
        subOnSignIn();
        configurePurchases(session.user.id);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, sess) => {
      setSession(sess || null);
      setUser(sess?.user || null);

      if (event === 'SIGNED_IN' && sess?.user) {
        setStatus('signed-in');
        // Switch local brief storage to this user's bucket IMMEDIATELY so
        // the next briefStore read does not leak the previous user's data.
        briefStore.setUser(sess.user.id);
        // Reconfigure RevenueCat for the new App User ID and pull
        // entitlements. Run in parallel — neither blocks the migration.
        configurePurchases(sess.user.id);
        subOnSignIn();
        setMigration({ state: 'running', error: null });
        try {
          await migrateGuestData(sess.user.id);
          setMigration({ state: 'ok', error: null });
        } catch (err) {
          console.error('[auth] migration failed', err);
          setMigration({ state: 'failed', error: err?.message || String(err) });
        }
        // Invalidate every cached query so new server data populates.
        queryClient.invalidateQueries();
      } else if (event === 'SIGNED_OUT') {
        setStatus('guest');
        // Revert to the guest bucket. Prior user's briefs stay in their
        // own namespaced key so they come back on re-login.
        briefStore.setUser('guest');
        // Clear paywall + entitlements + RC session.
        subOnSignOut();
        logoutPurchases();
        queryClient.clear();
      } else if (event === 'TOKEN_REFRESHED') {
        // no-op
      } else if (event === 'USER_UPDATED') {
        // no-op
      }
    });

    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email, password) => {
    // Sign-up confirmation now uses a 6-digit OTP code, not a link.
    //
    // Two failure modes we explicitly surface:
    //
    //   A) Email already registered — Supabase's signUp deliberately
    //      succeeds for already-registered emails (anti-enumeration).
    //      The tell is `data.user.identities.length === 0`. We throw
    //      EMAIL_EXISTS so AuthScreen can bounce to sign-in.
    //
    //   B) "Confirm email" is OFF in the Supabase project — signUp
    //      returns a usable session immediately (`data.session` is
    //      non-null), no email is sent, the user is auto-verified.
    //      This bypasses the OTP flow entirely and creates a new
    //      account every time even after a previous deletion. We
    //      detect that state, sign the user back out, and throw an
    //      explicit error so the developer sees what's wrong instead
    //      of silently landing on a logged-in dashboard.
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    const identities = data?.user?.identities;
    if (Array.isArray(identities) && identities.length === 0) {
      const e = new Error('An account already exists for this email. Sign in instead.');
      e.code = 'EMAIL_EXISTS';
      throw e;
    }

    if (data?.session) {
      // Sanity sign-out so the auto-verified session doesn't stick around
      // and confuse the UI.
      try { await supabase.auth.signOut(); } catch {}
      const e = new Error('Email confirmation is not enabled on this Supabase project. Turn it on under Authentication settings, then try again.');
      e.code = 'CONFIRMATION_DISABLED';
      throw e;
    }
  }, []);

  // Verify a sign-up OTP code typed into the app. Wraps Supabase's
  // verifyOtp with type:'signup'. On success Supabase auto-creates the
  // session and the auth state listener above flips us to 'signed-in',
  // so the caller doesn't need to do anything else.
  const verifySignupOtp = useCallback(async (email, token) => {
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: (token || '').trim(),
      type: 'signup',
    });
    if (error) throw error;
  }, []);

  // Re-send the confirmation email for a given address. Used by the
  // verify-code screen when the user wants a fresh code.
  const resendConfirmation = useCallback(async (email) => {
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPassword = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${location.origin}/reset-password`,
    });
    if (error) throw error;
  }, []);

  const retryMigration = useCallback(async () => {
    if (!user) return;
    setMigration({ state: 'running', error: null });
    try {
      await migrateGuestData(user.id);
      setMigration({ state: 'ok', error: null });
      queryClient.invalidateQueries();
    } catch (err) {
      setMigration({ state: 'failed', error: err?.message || String(err) });
    }
  }, [user]);

  return { status, user, session, signIn, signUp, verifySignupOtp, signOut, resetPassword, resendConfirmation, migration, retryMigration };
}
